import { Component, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';

import { Subject } from 'rxjs';
import dayjs, { Dayjs } from 'dayjs/esm';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import {
  DaterangepickerDirective,
  LocaleConfig,
} from 'ngx-daterangepicker-material';
import { EventEmitter } from '@angular/core';
import {
  DateRange,
  DateRanges,
  TimePeriod,
} from 'ngx-daterangepicker-material/daterangepicker.component';
import { DataService, ServicecallService } from 'src/app';
import utc from 'dayjs/esm/plugin/utc';

dayjs.extend(utc);

declare module 'dayjs/esm' {
  interface Dayjs {
    utc(): Dayjs;
    local(): Dayjs;
    isUTC(): boolean;
  }
}

export type DateRangeConfig = {
  unit: string;
  startOf: boolean;
  subtract: number;
  setHour: number;
  setMinute: number;
  useCurrentTime: boolean;
  endOfDay: boolean;
  endOfWeek: boolean;
  endOfMonth: boolean;
};
@Component({
  selector: 'app-datetime-range',
  templateUrl: './datetime-range.component.html',
  styleUrls: ['./datetime-range.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DatetimeRangeComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DatetimeRangeComponent,
    },
  ],
})
export class DatetimeRangeComponent implements OnInit {
  @ViewChild(DaterangepickerDirective, { static: false })
  pickerDirective: DaterangepickerDirective;

  @Input() appearance: MatFormFieldAppearance = 'outline';

  @Input() label: string | null = null;

  @Input() autoApply: boolean = true;

  @Input() closeOnAutoApply: boolean = true;

  @Input() linkedCalendars: boolean = true;

  @Input() minDate: Dayjs; // set the minimal and maximal date, these options are either a dayjs date or string in ISO format

  @Input() maxDate; // set the minimal and maximal date, these options are either a dayjs date or string in ISO format

  @Input() showDropdowns: boolean = true;

  @Input() showWeekNumbers: boolean = false;

  @Input() showISOWeekNumbers: boolean = false;

  @Input() showClearButton: boolean = false;

  @Input() showCancel: boolean = false;

  @Input() dateLimit: number;

  @Input() placeholder: string = '';

  // Range
  @Input() showCustomRangeLabel: boolean = false;

  @Input() alwaysShowCalendars: boolean = false; // set to true if you want to display the ranges with the calendar

  @Input() keepCalendarOpeningWithRange: boolean = false; // set to true if you want the calendar won't be closed after choosing a range

  @Input() showRangeLabelOnInput: boolean = false; // set to true if you want to display the range label on input

  @Input() customRangeDirection: boolean = false; // set to true if you want to allow selection range from end date first

  @Input() lockStartDate: boolean = false; // set to true if you want to lock start date and change only the end date

  // Timepicker
  @Input() timePicker: boolean = false; // set the attribute timePicker to true if you want to enable the timepicker.

  @Input() timePicker24Hour: boolean = false; // set to true if you want to set the timepicker to 24h instead of having AM and PM

  @Input() timePickerIncrement: number = 1; // set the value increment of the minutes (eg: for 12 there would be 0mn, 15mn, 30mn, 45mn,)

  @Input() timePickerSeconds: boolean = false; // set true if you want do display second's select

  // Customisation
  @Input() firstMonthDayClass: string; // add a custom class for all first day of the month

  @Input() lastMonthDayClass: string; // add a custom class for all last day of the month

  @Input() emptyWeekRowClass: string; // add a custom class in a row with a date in a week not in the current month

  @Input() emptyWeekColumnClass: string; // add a custom class for all date in a week not in the current month

  @Input() lastDayOfPreviousMonthClass: string = 'last-previous-day'; // add a custom class for the last day of the previous month

  @Input() firstDayOfNextMonthClass: string = 'first-next-day'; // add a custom class for the first day of the next month

  // Icon
  @Input() iconOnly: boolean = false;

  // Positioning
  @Input() opens: 'left' | 'center' | 'right' = 'right'; // position the calendar from the input element

  @Input() drops: 'up' | 'down' = 'down'; // position the calendar to the up or down of the calendar

  @Input() isDisabled: boolean;

  @Output() valueChanged = new Subject<TimePeriod>();

  @Output() clearClicked = new Subject<boolean>();

  @Output() rangeClicked = new Subject<DateRange>();
  @Output() datePassed: EventEmitter<any> = new EventEmitter();
  @Output() rangeSelected: EventEmitter<any> = new EventEmitter();
  locale: LocaleConfig = {
    format: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    displayFormat: 'D MMM, YYYY  HH:mm:ss',
    separator: '   -   ',
    cancelLabel: 'Cancel',
    applyLabel: 'Apply',
    clearLabel: 'Clear',
    customRangeLabel: 'Custom Range',
  };

  // Preset
  // ranges: DateRanges = {
  //   ['Today']: [dayjs().startOf('days'), dayjs()],
  //   ['Yesterday']: [dayjs().subtract(1, 'days').startOf('days'), dayjs().subtract(1, 'days').endOf('days')],
  //   ['Last 7 Days']: [dayjs().subtract(6, 'days').startOf('days'), dayjs()],
  //   ['Last 30 Days']: [dayjs().subtract(29, 'days').startOf('days'), dayjs()],
  //   ['This Month']: [dayjs().startOf('month'), dayjs()],
  //   ['Last Month']: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
  //   ['Last 3 Month']: [dayjs().subtract(3, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
  // };
  value: TimePeriod;
  // Control Value Accessor
  private _touched = false;
  private _disabled = false;
  private _validationStatus: ValidationErrors | null = null;
  public _selectedRange: object;
  private dateRangesConfig: any;
  private tenantConfig: any;
  ranges: any;
  public _selectedLabel : string;

  constructor(
    private _service: ServicecallService,
    private _dataSer: DataService
  ) { }
  disableFutureDates = (date: any) => {
    const currentDate = dayjs().startOf('day');
    const dateToCheck = dayjs(date.toISOString()).startOf('day');

    // This will allow the current date but disable future dates
    return dateToCheck.isAfter(currentDate, 'day');
  };
  ngOnInit(): void {
    this.dateRangesConfig =
      this._service.getConfiguration('dateTimeRanges') ?? null;
    this.tenantConfig =
      this._service.getConfiguration('tenantGenericConfig') ?? null;
    this.refreshRanges();
  }

  refreshRanges() {
    const defaultTimeRange = this.tenantConfig?.defaultTimeRange ?? 0;
    this.ranges = Object.keys(this.dateRangesConfig?.dateRanges)?.reduce(
      (acc, key) => {
        const rangeConfig = this.dateRangesConfig.dateRanges[key];
        const startDate = this.calculateDateRange(rangeConfig.start, defaultTimeRange);
        const endDate = this.calculateDateRange(rangeConfig.end, defaultTimeRange);
        acc[key] = [startDate, endDate];
        return acc;
      },
      {}
    );
  }



  calculateDateRange(
    config: DateRangeConfig,
    currentHour: number = 0
  ): dayjs.Dayjs {
    let baseDate = dayjs();

    const todayStart = dayjs().startOf('day').add(currentHour, 'hour'); // Today at `currentHour`
    const yesterdayStart = todayStart.subtract(1, 'day'); // Yesterday at `currentHour`

    if (baseDate.isBefore(todayStart)) {
      baseDate = yesterdayStart;
    } else {
      baseDate = todayStart;
    }

    if (config.subtract) {
      baseDate = baseDate.subtract(
        config.subtract,
        config.unit as dayjs.ManipulateType
      );
    }

    if (config.startOf) {
      baseDate = baseDate.startOf(config.unit as dayjs.OpUnitType);
    }

    if (!config.useCurrentTime) {
      if (config.setHour !== undefined && config.setMinute !== undefined) {
        baseDate = baseDate
          .set('hour', config.setHour)
          .set('minute', config.setMinute);
      }
    }

    if (config.useCurrentTime) {
      baseDate = dayjs();
    }

    if (config.endOfDay) {
      baseDate = baseDate.endOf('day');
    }

    if (config.endOfWeek) {
      baseDate = baseDate.endOf('week');
    }

    if (config.endOfMonth) {
      baseDate = baseDate.endOf('month');
    }

    return baseDate;
  }
// Events
onRangeClicked(value: DateRange) {
  this._selectedLabel = value?.label;
  this.rangeClicked.next(value);
  this._selectedRange = value;
  this.refreshRanges();
  this.value = {startDate: this.ranges?.[this._selectedLabel]?.[0], endDate: this.ranges?.[this._selectedLabel]?.[1]};
}

//To align the time difference while comparing values to get label (Custom)
toDayjsUTC(value: any) {
  if (dayjs.isDayjs(value)) {
    return value;
  } else {
    return dayjs(value).utc()?.add(5, 'hour').add(30, 'minute');
  }
}

onDatesUpdated(value: any) {
  const rangeSelected = {...this._selectedRange};
  this._selectedRange = {
    dates: [value?.startDate, value?.endDate],
    label: rangeSelected?.['label']
  }
  this._dataSer.broadcast('selectedDateRange', this._selectedRange);
  if (value?.startDate && value?.endDate) {
    this.valueChanged.next(this.value);
    // this.value = value;
    this.datePassed.emit(value);
    this._onChange(value);
    const selectedRange = this.value?.startDate && this.value?.endDate;
    if (selectedRange) {
      // Check if it's a custom range (i.e., not a predefined range)
      let isCustomRange = true;

      Object.keys(this.ranges).forEach((key) => {
        const [start, end] = this.ranges[key];
        // Compare start and end dates
        if (
          this.toDayjsUTC(this.value.startDate).isSame(start) &&
          this.toDayjsUTC(this.value.endDate).isSame(end)
        ) {
          isCustomRange = false;
        }
      });

      if (isCustomRange) {
        this._selectedRange = {
          label: 'Custom',
          dates: [this.value?.startDate, this.value?.endDate],
        };
        this._dataSer.broadcast('selectedDateRange', this._selectedRange);
      }
    }
    this._selectedLabel = this._selectedRange?.['label'];
    this.rangeSelected.emit(this._selectedRange);
  }
}

  onClearClicked() {
    this.clearClicked.next(true);
  }

  onIconClick(event?: Event) {
    this.refreshRanges();
    if(this._selectedLabel) {
      this.value = this._selectedLabel && {startDate: this.ranges?.[this._selectedLabel]?.[0], endDate: this.ranges?.[this._selectedLabel]?.[1]} ;
    }
    this.pickerDirective.open(event);
  }
  // _onChange: Function = Function;
  _onChange: Function = () => { };

  _onTouched = () => { };

  writeValue(value: TimePeriod) {
    this.value = value;
  }

  registerOnChange(onChange: any) {
    // this._onChange = () => onChange(this.value);
    this._onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this._onTouched = onTouched;
  }

  markAsTouched() {
    if (!this._touched) {
      this._onTouched();
      this._touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this._disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this._validationStatus = null;
    return this._validationStatus;
  }
}
